import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Input, Select } from '@st-operations/uikit/src/v2'
import pretty from '@st-operations/uikit/lib/pretty.js'

import standardSelectProps from '_lib/standardSelectProps'
import ValidatedForm from '_components/validated-form/index.jsx'
import CustomCheckbox from '_components/custom-checkbox/index.jsx'

import { toDateInputValue } from './utils'

import './styles.scss'

/**
 * A form component for configuring delivery frequency settings in the unified user profile.
 * Allows users to specify sample/non-sample delivery, frequency (daily/monthly),
 * mode (daily/aggregated), and date range for sample deliveries.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.data - The form data object containing frequency settings
 * @param {Function} props.onDataChange - Callback function when form data changes
 * @param {boolean} [props.requiredErrorFrequency] - Show required error for frequency field
 * @param {boolean} [props.requiredErrorMode] - Show required error for mode field
 * @param {boolean} [props.requiredErrorFrequencyStartDate] - Show required error for start date
 * @param {boolean} [props.requiredErrorFrequencyEndDate] - Show required error for end date
 */
const UUPDeliveryFrequencyForm = ({
  data = {},
  onDataChange,
  requiredErrorFrequency,
  requiredErrorMode,
  requiredErrorFrequencyStartDate,
  requiredErrorFrequencyEndDate,
}) => {
  const isSample = useMemo(() => data.isSample, [data])

  const frequencyOptions = useMemo(
    () =>
      Object.keys(paramCombinationRules[`${data.isSample === true}`]?.frequency || {}).map(
        (option) => ({
          label: option ? pretty(option) : option,
          value: option,
        })
      ),
    [data]
  )

  const modeOptions = useMemo(
    () =>
      Object.keys(
        paramCombinationRules[`${data.isSample === true}`]?.frequency?.[data.frequency]?.mode || {}
      ).map((option) => ({
        label: option ? pretty(option) : option,
        value: option,
      })),
    [data]
  )

  function handleChange(value, name) {
    const newData = { ...data, [name]: value }
    if (name === 'isSample') {
      newData.frequency = frequencyOptions[0].value
    }
    onDataChange?.(newData)
  }

  const fromToDates = [
    {
      label: 'Date From',
      required: requiredErrorFrequencyStartDate,
      validationClassName: 'uup-delivery-frequency-form__required--frequency',
      show: isSample,
      // eslint-disable-next-line react/display-name
      render: () => (
        <DateField
          label="From"
          description="Includes the specified events starting from the chosen date"
          name="startDate"
          value={toDateInputValue(data.startDate)}
          onChange={handleChange}
          max={toDateInputValue(data.endDate)}
          requiredError={requiredErrorFrequencyStartDate}
        />
      ),
    },
    {
      label: 'Date To',
      required: requiredErrorFrequencyEndDate,
      validationClassName: 'uup-delivery-frequency-form__required--frequency',
      show: isSample,
      // eslint-disable-next-line react/display-name
      render: () => (
        <DateField
          label="To"
          description="Includes the specified events up to the chosen end date"
          name="endDate"
          value={toDateInputValue(data.endDate)}
          onChange={handleChange}
          requiredError={requiredErrorFrequencyEndDate}
        />
      ),
    },
  ]

  return (
    <ValidatedForm
      inputs={[
        {
          label: 'Is Sample',
          render: () => (
            <>
              <span className={classNames('uup-delivery-frequency-form__content-label', '!mt-0')}>
                Is Sample
              </span>
              <CustomCheckbox
                className="ml-6"
                checked={data.isSample || false}
                onChange={(e) => handleChange(e.target.checked, 'isSample')}
              />
            </>
          ),
        },
        {
          label: 'Frequency',
          required: requiredErrorFrequency,
          render: () => (
            <>
              <span className="uup-delivery-frequency-form__content-label">Frequency</span>
              <Select
                className={classNames(
                  'uup-delivery-frequency-form__content-select',
                  'uup-delivery-frequency-form__content-no-margin'
                )}
                name="frequency"
                value={data.frequency}
                options={frequencyOptions}
                onChange={(_, name, value) => handleChange(value, name)}
                pretty
                {...standardSelectProps}
              />
            </>
          ),
        },
        modeOptions.length
          ? {
              label: 'Mode',
              required: requiredErrorMode,
              render: () => (
                <>
                  <span className="uup-delivery-frequency-form__content-label">Mode</span>
                  <Select
                    className={classNames(
                      'uup-delivery-frequency-form__content-select',
                      'uup-delivery-frequency-form__content-no-margin',
                      'ml-14'
                    )}
                    name="mode"
                    value={data.mode || 'daily'}
                    options={modeOptions}
                    onChange={(_, name, value) => handleChange(value, name)}
                    pretty
                    {...standardSelectProps}
                  />
                </>
              ),
            }
          : null,

        ...fromToDates,
      ].filter(Boolean)}
    />
  )
}

const paramCombinationRules = {
  false: {
    frequency: {
      daily: true,
      monthly: true,
    },
  },
  true: {
    frequency: {
      daily: {
        mode: {
          daily: true,
          aggregated: true,
        },
      },
    },
  },
}

UUPDeliveryFrequencyForm.propTypes = {
  data: PropTypes.object.isRequired,
  onDataChange: PropTypes.func.isRequired,
  requiredErrorFrequency: PropTypes.bool,
  requiredErrorMode: PropTypes.bool,
  requiredErrorFrequencyStartDate: PropTypes.bool,
  requiredErrorFrequencyEndDate: PropTypes.bool,
}

const DateField = ({ label, description, name, value, onChange, max, requiredError }) => {
  return (
    <>
      <span
        className={classNames(
          'uup-delivery-frequency-form__content-label',
          'uup-delivery-frequency-form__content-label--frequency-onetime'
        )}
      >
        {label}
      </span>
      <div className="uup-delivery-frequency-form__content-select">
        <Input
          className={classNames('!m-0', {
            'uup-delivery-frequency-form__required-field': requiredError,
          })}
          type="date"
          name={name}
          placeholder="YYYY-MM-DD"
          value={value}
          onChange={onChange}
          max={max}
          onFocus={(e) => {
            e.target.showPicker()
          }}
        />
        <If condition={description}>
          <div className="collapsible-section__description mt-2">{description}</div>
        </If>
      </div>
    </>
  )
}

DateField.propTypes = {
  label: PropTypes.string.isRequired,
  description: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  max: PropTypes.string,
  requiredError: PropTypes.bool,
}

export default UUPDeliveryFrequencyForm
